import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import { useChatbotAttachmentsOptional } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { useChatbotReferenceActionsOptional } from 'features/chatbot/contexts/ChatbotReferenceActionsContext'
import { useCallback } from 'react'

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Opens instrument references when no document-viewer UX context is mounted:
 * page-provided actions first, otherwise navigation to the ad-hoc chart view.
 */
export function useInstrumentReferenceFallback(): (
  entryIds: string[],
  timeWindow?: { from: string; to: string }
) => void {
  const referenceActions = useChatbotReferenceActionsOptional()
  const chatbotAttachments = useChatbotAttachmentsOptional()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const search = useRouterState({
    select: (state) => state.location.search,
  }) as Record<string, unknown>

  return useCallback(
    (entryIds, timeWindow) => {
      if (entryIds.length === 0) return

      const effectiveWindow =
        timeWindow ??
        chatbotAttachments?.attachments.find((a) => a.type === 'timeWindow')
          ?.timeWindow

      if (referenceActions) {
        referenceActions.addInstruments(entryIds, effectiveWindow)
        return
      }

      const siteId =
        getStringValue(params.siteID) ??
        getStringValue(search.siteId) ??
        getStringValue(search.siteID)
      if (!siteId) return

      const orgId =
        getStringValue(params.orgID) ??
        getStringValue(search.orgId) ??
        getStringValue(search.orgID)

      void navigate({
        to: '/process-data/view',
        search: {
          orgId,
          siteId,
          sensorIds: entryIds.join(','),
          start: effectiveWindow?.from,
          end: effectiveWindow?.to,
        },
      })
    },
    [referenceActions, chatbotAttachments, navigate, params, search]
  )
}
